import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceUsers } from '../../../hooks/useWorkspaceUsers.js';
import type { WorkspaceUser } from '../../../types/api.js';

export interface UserChipPickerProps {
  /** 選択中の Google Workspace user id 配列 */
  value: string[];
  /** 選択変更ハンドラ */
  onChange: (next: string[]) => void;
  /** スクリーンリーダー用ラベル */
  ariaLabel: string;
  /** 入力欄プレースホルダー */
  placeholder?: string;
  /** suggest dropdown の最大表示件数 */
  maxSuggestions?: number;
}

/**
 * 表示用のユーザー型。`workspace user` にマッチしない id (退職者 / 旧 slackId 等) は
 * `unknown: true` として元の id をそのまま表示する。
 * @graph-connects none
 */
interface DisplayUser {
  id: string;
  primary: string;
  secondary?: string;
  unknown?: boolean;
}

/**
 * 単一の WorkspaceUser を表示用に変換する
 * @graph-connects none
 */
function toDisplayUser(user: WorkspaceUser): DisplayUser {
  const secondary = user.nickName ? `${user.email} · ${user.nickName}` : user.email;
  return { id: user.id, primary: user.name, secondary };
}

/**
 * 未解決 id (旧 slackId / 退職者 / 解決失敗) の表示用ヘルパ。secondary は表示時に翻訳する
 * @param id 解決できなかった id
 * @param unresolvedLabel 「(未解決)」の表示文言
 * @graph-connects none
 */
function toUnknownDisplayUser(id: string, unresolvedLabel: string): DisplayUser {
  return { id, primary: id, secondary: unresolvedLabel, unknown: true };
}

/**
 * value (id 配列) を WorkspaceUser リストと突き合わせて表示用に展開する
 * @graph-connects none
 */
function buildSelected(
  value: string[],
  users: WorkspaceUser[],
  unresolvedLabel: string,
): DisplayUser[] {
  const byId = new Map(users.map((u) => [u.id, u]));
  const bySlackId = new Map<string, WorkspaceUser>();
  for (const u of users) {
    if (u.slackId) bySlackId.set(u.slackId, u);
  }
  const byEmail = new Map(users.map((u) => [u.email.toLowerCase(), u]));
  return value.map((id) => {
    const u = byId.get(id) ?? bySlackId.get(id) ?? byEmail.get(id.toLowerCase());
    return u ? toDisplayUser(u) : toUnknownDisplayUser(id, unresolvedLabel);
  });
}

/**
 * 検索クエリで部分一致候補をフィルタする
 * @graph-connects none
 */
function filterSuggestions(
  users: WorkspaceUser[],
  query: string,
  selected: Set<string>,
  max: number,
): DisplayUser[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits: DisplayUser[] = [];
  for (const u of users) {
    if (selected.has(u.id)) continue;
    const haystack = [u.name, u.email, u.nickName ?? '', u.slackId ?? ''].join(' ').toLowerCase();
    if (haystack.includes(q)) {
      hits.push(toDisplayUser(u));
      if (hits.length >= max) break;
    }
  }
  return hits;
}

/**
 * UserChipPicker
 *
 * 検索付き複数選択ピッカー。入力すると nickname / email / name の部分一致で候補を suggest し、
 * クリック (or Enter) で chip 化して `value` に追加。chip の × で削除。
 *
 * 内部値は **Google Workspace user id** (string)。表示は workspace user 名簿と突き合わせて
 * 名前 + email を見せる。名簿にマッチしない id (旧 slackId / 退職者) は `(未解決)` chip として
 * 残す (削除可能、追加は不可)。
 *
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business pipeline-permissions の admins/operators 編集用に検索+chip 化の多選択ピッカーを提供
 * @graph-connects useWorkspaceUsers [calls] 候補の workspace user 名簿を取得
 */
export function UserChipPicker({
  value,
  onChange,
  ariaLabel,
  placeholder,
  maxSuggestions = 12,
}: UserChipPickerProps) {
  const { t } = useTranslation();
  const { data: users = [] } = useWorkspaceUsers();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const selectedSet = useMemo(() => new Set(value), [value]);
  const unresolvedLabel = t('userChipPicker.unresolved');
  const selected = useMemo(
    () => buildSelected(value, users, unresolvedLabel),
    [value, users, unresolvedLabel],
  );
  const suggestions = useMemo(
    () => filterSuggestions(users, query, selectedSet, maxSuggestions),
    [users, query, selectedSet, maxSuggestions],
  );

  const addUser = (id: string): void => {
    if (selectedSet.has(id)) return;
    onChange([...value, id]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeUser = (id: string): void => {
    onChange(value.filter((v) => v !== id));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && suggestions[0]) {
        addUser(suggestions[0].id);
      }
    } else if (e.key === 'Backspace' && query.length === 0 && value.length > 0) {
      const lastValue = value[value.length - 1];
      if (lastValue !== undefined) {
        removeUser(lastValue);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div
        className="
          flex flex-wrap items-center gap-1.5 min-h-[40px] w-full
          rounded-lg border border-[var(--color-border)]
          bg-[var(--color-bg-elevated)] px-2 py-1.5
          focus-within:border-[var(--color-border-focus)]
        "
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((s) => (
          <span
            key={s.id}
            className={`
              inline-flex items-center gap-1.5
              rounded-md px-2 py-0.5 text-xs
              ${
                s.unknown
                  ? 'bg-[var(--color-warning-muted)] text-[var(--color-warning)]'
                  : 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)]'
              }
            `}
          >
            <span title={s.secondary}>{s.primary}</span>
            <button
              type="button"
              aria-label={t('userChipPicker.remove', { name: s.primary })}
              onClick={(e) => {
                e.stopPropagation();
                removeUser(s.id);
              }}
              className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open && suggestions.length > 0}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="
            flex-1 min-w-[120px] bg-transparent border-0 outline-none
            text-sm text-[var(--color-text-primary)]
            placeholder:text-[var(--color-text-muted)]
          "
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // 候補クリックを拾うため少しだけ遅らせて閉じる
            setTimeout(() => {
              setOpen(false);
            }, 150);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="
            absolute z-10 mt-1 w-full max-h-64 overflow-auto
            rounded-lg border border-[var(--color-border)]
            bg-[var(--color-bg-surface)] shadow-lg
          "
        >
          {suggestions.map((s) => (
            <li key={s.id} role="option" aria-selected="false">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => {
                  addUser(s.id);
                }}
                className="
                  w-full text-left px-3 py-2
                  hover:bg-[var(--color-bg-hover)]
                  flex flex-col gap-0.5
                "
              >
                <span className="text-sm text-[var(--color-text-primary)]">{s.primary}</span>
                {s.secondary && (
                  <span className="text-xs text-[var(--color-text-secondary)]">{s.secondary}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
